(function () {
  function getUpsellData() {
    const el = document.getElementById('cart-upsells-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  // Aggregate scores across all cart items, filter out products already in cart, return top 10 IDs
  function getTopProductIds(data) {
    const cartIds = new Set(data.cartProductIds);
    const scores = {};

    for (const { id, score } of data.entries) {
      if (cartIds.has(id)) continue;
      scores[id] = (scores[id] || 0) + score;
    }

    return Object.entries(scores)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([id]) => id);
  }

  // Fetch product data from Shopify predictive search using id: filter
  async function fetchProductsByIds(ids) {
    if (!ids.length) return [];
    const query = ids.map((id) => `id:${id}`).join(' OR ');
    try {
      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`
      );
      if (!res.ok) return [];
      const json = await res.json();
      return json.resources?.results?.products ?? [];
    } catch {
      return [];
    }
  }

  function formatMoney(price) {
    if (!price && price !== 0) return '';
    const amount = parseFloat(price);
    const currency = window.Shopify?.currency?.active || 'GBP';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  function getOptimizedImageUrl(product) {
    const url = product.featured_image?.url;
    if (!url) return '';
    return url.includes('?') ? `${url}&width=160` : `${url}?width=160`;
  }

  // Extract variant ID from search result. The predictive search sometimes omits
  // variants entirely — we also try parsing it from the variant URL as a fallback.
  function getVariantId(product) {
    const directId = product.variants?.[0]?.id;
    if (directId) return String(directId);
    const variantUrl = product.variants?.[0]?.url || '';
    const match = variantUrl.match(/[?&]variant=(\d+)/);
    return match ? match[1] : '';
  }

  function renderCard(product) {
    const variantId = getVariantId(product);
    const imgUrl = getOptimizedImageUrl(product);
    const title = product.title || '';
    const price = formatMoney(product.price);
    const handle = product.handle || '';

    return `
      <div class="cart-upsell-card">
        <a href="${product.url}" class="cart-upsell-card__img-wrap" tabindex="-1" aria-hidden="true">
          ${
            imgUrl
              ? `<img src="${imgUrl}" alt="${title}" class="cart-upsell-card__img" loading="lazy" width="80" height="80">`
              : '<div class="cart-upsell-card__img-placeholder"></div>'
          }
        </a>
        <div class="cart-upsell-card__info">
          <a href="${product.url}" class="cart-upsell-card__title">${title}</a>
          <span class="cart-upsell-card__price">${price}</span>
        </div>
        <button
          class="cart-upsell-card__btn"
          data-variant-id="${variantId}"
          data-handle="${handle}"
          data-product-url="${product.url}"
          aria-label="Add ${title} to cart"
        >Add</button>
      </div>
    `;
  }

  // Use a generation counter to discard stale async renders
  let renderGen = 0;

  async function renderUpsells() {
    const wrapper = document.getElementById('cart-upsells');
    if (!wrapper) return;

    const data = getUpsellData();
    if (!data?.entries?.length) {
      wrapper.hidden = true;
      return;
    }

    const topIds = getTopProductIds(data);
    if (!topIds.length) {
      wrapper.hidden = true;
      return;
    }

    const gen = ++renderGen;
    const products = await fetchProductsByIds(topIds);
    if (gen !== renderGen) return; // A newer render started; discard this one

    const sorted = topIds
      .map((id) => products.find((p) => String(p.id) === id))
      .filter(Boolean);

    if (!sorted.length) {
      wrapper.hidden = true;
      return;
    }

    const grid = wrapper.querySelector('.cart-upsells__grid');
    if (grid) grid.innerHTML = sorted.map(renderCard).join('');
    wrapper.hidden = false;
  }

  const debouncedRender = debounce(renderUpsells, 200);

  async function resolveVariantId(btn) {
    const fromData = Number(btn.dataset.variantId);
    if (fromData) return fromData;

    // Predictive search omitted variants — fetch product JSON by handle
    const handle = btn.dataset.handle;
    if (!handle) return null;
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      return data.variants?.[0]?.id || null;
    } catch {
      return null;
    }
  }

  async function handleAddToCart(btn) {
    if (btn.disabled) return;

    btn.disabled = true;
    btn.textContent = '...';

    const variantId = await resolveVariantId(btn);
    if (!variantId) {
      // No variant available — send user to the product page instead
      window.location.href = btn.dataset.productUrl || '/';
      return;
    }

    try {
      const res = await fetch('/cart/add.js', {
        ...fetchConfig(),
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!res.ok) throw new Error('add_failed');

      btn.textContent = 'Added!';

      // Directly refresh cart drawer items, footer, and icon bubble
      fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          for (const selector of ['cart-drawer-items', '.cart-drawer__footer']) {
            const target = document.querySelector(selector);
            const source = html.querySelector(selector);
            if (target && source) target.replaceWith(source);
          }
        })
        .catch(() => {});

      fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`)
        .then((r) => r.text())
        .then((html) => {
          const bubble = document.getElementById('cart-icon-bubble');
          if (!bubble) return;
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const source = parsed.querySelector('.shopify-section');
          if (source) bubble.innerHTML = source.innerHTML;
        })
        .catch(() => {});

      setTimeout(() => {
        btn.textContent = 'Add';
        btn.disabled = false;
      }, 1500);
    } catch {
      btn.textContent = 'Add';
      btn.disabled = false;
    }
  }

  function init() {
    // Observe the cart-drawer custom element (never destroyed) with subtree so we
    // catch ALL update paths:
    //   • renderContents() from PDP add-to-cart → replaces #CartDrawer.innerHTML
    //   • updateQuantity()                       → replaces .drawer__inner.innerHTML
    //   • onCartUpdate()                         → replaces cart-drawer-items via replaceWith()
    // Filter out mutations we caused (updating #cart-upsells content) to avoid loops.
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl) {
      const observer = new MutationObserver((mutations) => {
        const isOwnUpdate = mutations.every(
          (m) => m.target.id === 'cart-upsells' || !!m.target.closest?.('#cart-upsells')
        );
        if (!isOwnUpdate) debouncedRender();
      });
      observer.observe(cartDrawerEl, { childList: true, subtree: true });
    }

    // Event delegation for add-to-cart buttons
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.cart-upsell-card__btn');
      if (btn) handleAddToCart(btn);
    });

    renderUpsells();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
